import { createModuleRegistry, type GameModule } from '@md/engine';
import { magneticDuo } from '@md/game-magnetic-duo';
import {
  type ContentCatalog,
  DISCONNECT_GRACE_SECONDS,
  DRAFTS_TOKEN_STORAGE_KEY,
  MAGNETIC_DUO_GAME_ID,
  NET_SESSION_STORAGE_KEY,
  type NetStatusSnapshot,
  type PlayerIndex,
  type PlayerInputs,
  type ProgressState,
  RoomCodeSchema,
  type ScreenName,
  type SimStateSnapshot,
} from '@md/shared';
import { type AccountService, createAccountService } from './account/service';
import { type AudioReactor, createAudioReactor, createHaptics, createSfxPlayer } from './audio';
import { createLevelLibrary, type LevelLibrary } from './content/library';
import type { GameResult, GameSession } from './game/session';
import { NET_CONTROL_HINT, netKeyBindings } from './input/keyboard';
import { createLocalSession, type LocalSession } from './local/session';
import { createNetSession, type NetSession } from './net/session';
import { createProgressStore, nextLevelId, type ProgressStore } from './progress';
import { createCharSelectScreen } from './screens/char-select';
import { renderError } from './screens/error';
import { createGameScreen, type GameScreen } from './screens/game';
import { type HomeScreen, renderHome } from './screens/home';
import { createLevelSelectScreen } from './screens/level-select';
import { createLobbyScreen } from './screens/lobby';
import { createSettingsStore, type Settings, type SettingsStore } from './settings';
import { createConnectionBanner } from './ui/connection-banner';

/**
 * Coquille applicative du client.
 *
 * PHASE 3 : deux modes de jeu cohabitent — LOCAL (1 clavier, 2 joueurs, aucun
 * réseau) et RÉSEAU (room hébergée par le serveur, simulation autoritaire).
 * Le routage d'écrans est piloté par l'état réseau reçu du serveur : l'écran
 * affiché est toujours une pure fonction de cet état, jamais un effet de bord
 * d'un clic.
 *
 * Le jeu est atteint par le registre de modules : aucun `if (gameId === …)`
 * ne doit apparaître ici (spec §4).
 */
export interface ClientApp {
  /** Monte l'écran initial. */
  start(): void;
  /** Écran actuellement affiché. */
  screen(): ScreenName;
  /** État de simulation, `null` tant qu'aucune partie n'est en cours. */
  state(): SimStateSnapshot | null;
  /** État réseau observable (spec §9, extension phase 3). */
  net(): NetStatusSnapshot;
  /** Lance une partie locale sur un niveau (défaut : le 1er du catalogue). */
  play(level?: unknown): void;
  /** Ouvre le sélecteur de niveaux en mode local. */
  browseLevels(): void;
  /** Reprend au premier niveau débloqué non terminé. */
  continueGame(): void;
  /** Affiche la page d'erreur (adresse inconnue, panne irrécupérable). */
  showError(code: string, title: string, message: string): void;
  /** Catalogue de contenu chargé depuis l'API (`null` avant chargement). */
  catalog(): ContentCatalog | null;
  /** Progression du joueur (étoiles, déblocages) — extension des hooks (spec §9). */
  progress(): ProgressState;
  /** Efface la progression — outil de test et bouton « recommencer ». */
  resetProgress(): void;
  /** Hook de test : charge un niveau arbitraire en avance MANUELLE (spec §9). */
  loadLevel(level: unknown): void;
  setInputs(player: PlayerIndex, inputs: Partial<PlayerInputs>): void;
  step(ticks: number): void;
  seed(value: number): void;
  goHome(): void;
}

export interface ClientAppOptions {
  /** Modules disponibles — injectable pour les tests. */
  readonly modules?: readonly GameModule[];
  /** URL WebSocket — déduite de l'origine par défaut. */
  readonly wsUrl?: string;
  /** Stockage de la session réseau (reprise après rechargement). */
  readonly storage?: Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;
  /**
   * Stockage propre à l'ONGLET (`sessionStorage` par défaut). Il fait foi sur
   * le stockage partagé : deux onglets d'un même navigateur ne se disputent
   * plus la même place (limite connue de la phase 3).
   */
  readonly tabStorage?: Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;
  /** Bibliothèque de contenu (API REST) — injectable pour les tests. */
  readonly library?: LevelLibrary;
  /** Progression du joueur — injectable pour les tests. */
  readonly progress?: ProgressStore;
  /** Préférences (son, vibration) — injectables pour les tests. */
  readonly settings?: SettingsStore;
  /** Compte joueur optionnel — injectable pour les tests. */
  readonly account?: AccountService;
}

interface StoredSession {
  readonly code: string;
  readonly memberId: string;
  readonly token: string;
}

function readStoredSession(storage: Pick<Storage, 'getItem'> | undefined): StoredSession | null {
  const raw = storage?.getItem(NET_SESSION_STORAGE_KEY);
  if (!raw) {
    return null;
  }
  try {
    const parsed = JSON.parse(raw) as Partial<StoredSession>;
    if (
      typeof parsed.code === 'string' &&
      typeof parsed.memberId === 'string' &&
      typeof parsed.token === 'string'
    ) {
      return { code: parsed.code, memberId: parsed.memberId, token: parsed.token };
    }
  } catch {
    // Entrée corrompue : on l'ignore, l'utilisateur repartira de l'accueil.
  }
  return null;
}

export function createApp(root: HTMLElement, options: ClientAppOptions = {}): ClientApp {
  const registry = createModuleRegistry(options.modules ?? [magneticDuo]);
  const module = registry.require(MAGNETIC_DUO_GAME_ID);
  const local: LocalSession = createLocalSession(module);
  const storage = options.storage ?? safeStorage(() => window.localStorage);
  const tabStorage = options.tabStorage ?? safeStorage(() => window.sessionStorage);
  const draftsToken = readDraftsToken();
  const library: LevelLibrary =
    options.library ?? createLevelLibrary({ gameId: module.id, token: draftsToken });

  const progressStore: ProgressStore = options.progress ?? createProgressStore();
  const account: AccountService = options.account ?? createAccountService();
  const settingsStore: SettingsStore = options.settings ?? createSettingsStore();
  const initialSettings = settingsStore.get();
  const sfx = createSfxPlayer({ enabled: initialSettings.sound });
  const haptics = createHaptics({ enabled: initialSettings.haptics });
  const audio: AudioReactor = createAudioReactor({ sfx, haptics });
  settingsStore.subscribe((next) => {
    sfx.setEnabled(next.sound);
    haptics.setEnabled(next.haptics);
  });

  const net: NetSession = createNetSession({
    module,
    levels: library,
    ...(options.wsUrl === undefined ? {} : { url: options.wsUrl }),
  });

  let current: ScreenName = 'home';
  let activeSession: GameSession = local;
  let netGameOpen = false;
  let resultDismissed = false;
  let home: HomeScreen | null = null;
  /**
   * Code d'invitation en cours de reprise : on a tenté de récupérer SA place
   * (jeton) sur cette room ; si le serveur refuse, on rejoint normalement.
   */
  let pendingJoinCode: string | null = null;
  /** Lien d'invitation ouvert SANS session de compte : rejoint après connexion. */
  let pendingInviteCode: string | null = null;

  const gameScreen: GameScreen = createGameScreen({ onScreen: setScreen, audio });
  const connectionBanner = createConnectionBanner({
    onRetry: () => net.resume(),
    onHome: () => leaveRoom(),
  });
  const lobbyScreen = createLobbyScreen({ onLeave: () => leaveRoom() });
  const charScreen = createCharSelectScreen({
    characters: module.characters,
    onPick: (index) => net.pickChar(index),
    onLeave: () => leaveRoom(),
  });
  const levelScreen = createLevelSelectScreen({
    mode: 'net',
    onPickLevel: (levelId) => net.pickLevel(levelId),
    onReady: (ready) => net.setReady(ready),
    onLeave: () => leaveRoom(),
  });
  const localLevelScreen = createLevelSelectScreen({
    mode: 'local',
    onPickLevel: (levelId) => {
      void playLevelId(levelId);
    },
    onLeave: () => app.goHome(),
    leaveLabel: '← Accueil',
  });

  library.subscribe((catalog) => {
    levelScreen.setCatalog(catalog);
    localLevelScreen.setCatalog(catalog);
    localLevelScreen.setProgress(progressStore.state());
    home?.setCatalog(catalog);
  });

  progressStore.subscribe((state) => {
    localLevelScreen.setProgress(state);
    home?.setProgress(state);
  });

  account.subscribe((state) => {
    home?.setAccount(state);
  });

  /**
   * Pousse la progression locale vers le compte, puis absorbe la fusion que
   * renvoie le serveur (elle peut contenir les victoires d'un autre appareil).
   * Sans session ou hors ligne : silencieux, le localStorage reste la vérité.
   */
  async function pushProgress(): Promise<void> {
    const merged = await account.sync(progressStore.state());
    if (merged !== null) {
      progressStore.absorb(merged);
    }
  }

  async function authAccount(
    kind: 'register' | 'login',
    pseudo: string,
    password: string,
  ): Promise<void> {
    home?.setAccountError(null);
    const result =
      kind === 'register'
        ? await account.register(pseudo, password)
        : await account.login(pseudo, password);
    if (!result.ok) {
      home?.setAccountError(result.message);
      return;
    }
    // La progression du compte d'abord (absorbée localement), puis la
    // progression locale poussée : dans les deux sens, uniquement vers le haut.
    progressStore.absorb(result.progress);
    await pushProgress();
    if (pendingInviteCode !== null) {
      // Lien d'invitation ouvert avant la connexion : on rejoint maintenant.
      const code = pendingInviteCode;
      pendingInviteCode = null;
      joinRoom(code);
    }
  }

  /**
   * Enregistre une victoire et laisse la progression rouvrir ce qu'il faut.
   *
   * Le résultat vient de la session (autoritaire en réseau, cf. phase 3) : la
   * progression n'est jamais déduite d'un état prédit.
   */
  function recordWin(result: GameResult, levelId: string | undefined): void {
    if (!levelId) {
      return;
    }
    progressStore.record({
      levelId,
      stars: result.stars,
      timeMs: result.timeMs,
      bolts: result.bolts.collected,
      deaths: result.deaths,
    });
    void pushProgress();
  }

  function setScreen(screen: ScreenName): void {
    current = screen;
    root.dataset.screen = screen;
  }

  function show(screen: ScreenName, view: HTMLElement): void {
    setScreen(screen);
    if (root.firstChild !== view) {
      root.replaceChildren(view);
    }
  }

  function safeStorage(
    get: () => Storage,
  ): Pick<Storage, 'getItem' | 'setItem' | 'removeItem'> | undefined {
    try {
      return get();
    } catch {
      return undefined;
    }
  }

  function persistSession(status: NetStatusSnapshot): void {
    const identity = net.identity();
    if (!identity || !status.roomCode) {
      return;
    }
    const payload = JSON.stringify({
      code: identity.code,
      memberId: identity.memberId,
      token: identity.token,
    } satisfies StoredSession);
    // L'onglet d'abord (il fait foi), le stockage partagé ensuite (il survit à
    // la fermeture de l'onglet : écran verrouillé, navigateur tué).
    tabStorage?.setItem(NET_SESSION_STORAGE_KEY, payload);
    storage?.setItem(NET_SESSION_STORAGE_KEY, payload);
  }

  function forgetSession(): void {
    tabStorage?.removeItem(NET_SESSION_STORAGE_KEY);
    storage?.removeItem(NET_SESSION_STORAGE_KEY);
  }

  function leaveRoom(): void {
    forgetSession();
    pendingJoinCode = null;
    netGameOpen = false;
    resultDismissed = false;
    net.leave();
    app.goHome();
  }

  function pauseMessage(status: NetStatusSnapshot): string {
    if (status.pauseReason === 'peer-disconnected') {
      return `Votre partenaire s’est déconnecté. Reprise possible pendant ${DISCONNECT_GRACE_SECONDS} s…`;
    }
    return 'Partie mise en pause.';
  }

  function openNetGame(status: NetStatusSnapshot): void {
    const index = status.charIndex ?? 0;
    if (!netGameOpen) {
      gameScreen.configure({
        session: net,
        mode: 'net',
        bindings: netKeyBindings(index),
        touchPlayer: index,
        hints: [{ who: module.characters[index].name, keys: NET_CONTROL_HINT }],
        onReplay: () => net.requestRestart(),
        onExit: () => backToLobby(),
        onQuit: () => leaveRoom(),
        exitLabel: 'Retour au salon',
        roomLabel: `Partie ${status.roomCode ?? ''}`,
        ...(status.levelId === null ? {} : { levelId: status.levelId }),
        onWin: recordWin,
        onNext: null,
        // Recommencer (barre d'outils comme écran de victoire) : le serveur
        // relance le niveau pour les DEUX joueurs, décompte 3-2-1 compris.
        onRestart: () => net.requestRestart(),
      });
      activeSession = net;
      show('game', gameScreen.element);
      gameScreen.mount();
      netGameOpen = true;
    }
    gameScreen.setCountdown(status.phase === 'countdown' ? status.countdown : null);
    gameScreen.setPaused(status.paused, pauseMessage(status));
    if (status.result?.result === 'won' && !resultDismissed) {
      gameScreen.showResult(net.result());
    } else if (status.phase === 'countdown') {
      // Un décompte = une manche neuve : l'écran de victoire n'a plus cours.
      gameScreen.showResult(null);
    }
  }

  function backToLobby(): void {
    resultDismissed = true;
    closeNetGame();
    routeNet(net.status());
  }

  function closeNetGame(): void {
    if (!netGameOpen) {
      return;
    }
    netGameOpen = false;
    gameScreen.unmount();
  }

  /**
   * Bandeau « connexion perdue ».
   *
   * Règle : **aucun écran d'attente sans issue**. Tant que la socket est
   * tombée, l'utilisateur voit ce qui se passe et garde deux actions — réessayer
   * tout de suite, ou revenir à l'accueil.
   */
  function updateConnectionBanner(status: NetStatusSnapshot): void {
    const lost = Boolean(status.roomId) && !status.connected;
    if (!lost) {
      connectionBanner.remove();
      return;
    }
    if (connectionBanner.parentElement !== root) {
      root.append(connectionBanner);
    }
  }

  function routeNet(status: NetStatusSnapshot): void {
    routeScreen(status);
    // Après le rendu de l'écran : `show()` remplace le contenu de la racine, le
    // bandeau doit donc être ré-accroché ensuite.
    updateConnectionBanner(status);
  }

  function routeScreen(status: NetStatusSnapshot): void {
    if (!status.roomId) {
      closeNetGame();
      app.goHome();
      return;
    }
    // Seule une VICTOIRE ouvre l'écran de résultats : une partie interrompue
    // (partenaire parti) renvoie tout le monde au salon.
    const showingResult = status.result?.result === 'won' && !resultDismissed;
    if (status.phase === 'countdown' || status.phase === 'playing' || showingResult) {
      openNetGame(status);
      return;
    }
    closeNetGame();
    if (status.players.length < 2) {
      lobbyScreen.update(status);
      show('lobby', lobbyScreen.element);
      return;
    }
    const everyoneChose = status.players.every((player) => player.charIndex !== null);
    if (!everyoneChose) {
      charScreen.update(status);
      show('char-select', charScreen.element);
      return;
    }
    levelScreen.update(status);
    show('level-select', levelScreen.element);
  }

  net.onNetChange((kind, status) => {
    if (kind === 'gameStart') {
      resultDismissed = false;
    }
    if (kind === 'lobby' && status.roomId) {
      pendingJoinCode = null;
    }
    if (kind === 'error' && !status.roomId && pendingJoinCode !== null) {
      // La reprise de notre place a échoué (room repartie de zéro, jeton
      // périmé) : on rejoint alors la partie comme un nouvel arrivant, au lieu
      // d'afficher « aucune partie ne porte ce code » sur un lien valide.
      const code = pendingJoinCode;
      pendingJoinCode = null;
      forgetSession();
      net.connect();
      net.joinRoom(code);
      return;
    }
    if (kind === 'error' && !status.roomId) {
      // Erreur hors room (code inconnu, reprise impossible) : retour à
      // l'accueil, message affiché SUR le nouvel écran — `goHome()` en
      // reconstruit un, donc l'ordre compte.
      forgetSession();
      app.goHome();
      home?.setError(status.lastError?.message ?? 'erreur réseau');
      return;
    }
    if (status.roomId) {
      persistSession(status);
    } else if (kind === 'closed') {
      forgetSession();
    }
    routeNet(status);
    if (kind === 'closed' && status.lastError?.code === 'ROOM_CLOSED') {
      // Room fermée par le départ du partenaire : le dire sur l'accueil que
      // `routeNet` vient de reconstruire.
      home?.setError(status.lastError.message);
    }
  });

  const app: ClientApp = {
    start(): void {
      // Le serveur renvoie l'application pour toute route GET inconnue
      // (fallback SPA) : c'est ici qu'on distingue « / » d'une adresse
      // inventée, et qu'on affiche un 404 propre.
      const path = window.location.pathname;
      if (path !== '/' && path !== '/index.html') {
        app.showError(
          '404',
          'Page introuvable',
          `L’adresse « ${path} » ne correspond à aucun écran de Magnetic Duo.`,
        );
        return;
      }
      net.connect();
      app.goHome();
      // Le contenu vient de l'API (SQLite) : le catalogue est chargé une fois
      // au démarrage, puis republié à tous les écrans par l'abonnement.
      void library.load().catch((error: unknown) => {
        home?.setError(
          `Contenu indisponible : ${error instanceof Error ? error.message : String(error)}`,
        );
      });
      resumeOrPrefill();
      watchForegroundResume();
      // Session de compte mémorisée : on la revalide puis on synchronise la
      // progression (les victoires d'un autre appareil arrivent ici).
      void account.restore().then((alive) => (alive ? pushProgress() : undefined));
    },

    screen(): ScreenName {
      return current;
    },

    state(): SimStateSnapshot | null {
      return activeSession.snapshot();
    },

    net(): NetStatusSnapshot {
      return net.status();
    },

    play(level?: unknown): void {
      if (level !== undefined) {
        startLocalLevel(level);
        return;
      }
      const first = library.firstLevelId();
      if (first === undefined) {
        // Catalogue pas encore arrivé : on l'attend plutôt que d'échouer.
        void library
          .load()
          .then(() => {
            const id = library.firstLevelId();
            return id === undefined ? undefined : playLevelId(id);
          })
          .catch(() => home?.setError('Aucun niveau disponible.'));
        return;
      }
      void playLevelId(first);
    },

    browseLevels(): void {
      localLevelScreen.setCatalog(library.catalog());
      localLevelScreen.setProgress(progressStore.state());
      show('level-select', localLevelScreen.element);
    },

    /** Reprend au premier niveau débloqué et non terminé. */
    continueGame(): void {
      const catalog = library.catalog();
      const state = progressStore.state();
      const candidate = firstUnfinishedLevelId(catalog, state);
      if (candidate === undefined) {
        app.browseLevels();
        return;
      }
      void playLevelId(candidate);
    },

    progress(): ProgressState {
      return progressStore.state();
    },

    resetProgress(): void {
      progressStore.reset();
    },

    catalog(): ContentCatalog | null {
      return library.catalog();
    },

    loadLevel(level: unknown): void {
      local.setAuto(false);
      local.loadLevel(level);
      openLocalGame();
    },

    setInputs(player: PlayerIndex, inputs: Partial<PlayerInputs>): void {
      activeSession.setInputs(player, inputs);
    },

    step(ticks: number): void {
      if (activeSession !== local) {
        throw new Error('step() n’existe qu’en mode local : le serveur est autoritaire en réseau.');
      }
      local.step(ticks);
    },

    seed(value: number): void {
      local.seed(value);
    },

    showError(code: string, title: string, message: string): void {
      closeNetGame();
      gameScreen.unmount();
      activeSession = local;
      home = null;
      const screen = renderError({
        code,
        title,
        message,
        onHome: () => {
          // On revient à la racine : sans cela, un rechargement retomberait
          // sur la même adresse inconnue.
          window.history.replaceState(null, '', '/');
          app.goHome();
        },
      });
      show('error', screen.element);
    },

    goHome(): void {
      closeNetGame();
      gameScreen.unmount();
      activeSession = local;
      home = renderHome({
        settings: settingsStore.get(),
        onSettings: (patch: Partial<Settings>) => {
          settingsStore.set(patch);
          sfx.resume();
          sfx.play('ui');
        },
        onPlay: () => app.play(),
        onContinue: () => app.continueGame(),
        onBrowseLevels: () => app.browseLevels(),
        onCreateRoom: () => {
          home?.setError(null);
          net.connect();
          net.createRoom(module.id);
        },
        onJoinRoom: (code: string) => joinRoom(code),
        onAccountRegister: (pseudo, password) => {
          void authAccount('register', pseudo, password);
        },
        onAccountLogin: (pseudo, password) => {
          void authAccount('login', pseudo, password);
        },
        onAccountLogout: () => {
          void account.logout();
          // La progression appartient au compte : le miroir mémoire se vide.
          progressStore.reset();
        },
      });
      home.setCatalog(library.catalog());
      home.setProgress(progressStore.state());
      home.setAccount(account.state());
      show('home', home.element);
    },
  };

  /** Charge un niveau du catalogue, puis démarre la partie locale. */
  async function playLevelId(levelId: string): Promise<void> {
    const level = await library.ensure(levelId);
    if (!level) {
      home?.setError(`Niveau introuvable : ${levelId}`);
      app.goHome();
      return;
    }
    startLocalLevel(level);
  }

  function startLocalLevel(level: unknown): void {
    local.setAuto(true);
    local.loadLevel(level);
    openLocalGame();
  }

  /** Premier niveau publié débloqué et pas encore terminé (bouton « reprendre »). */
  function firstUnfinishedLevelId(
    catalog: ContentCatalog | null,
    state: ProgressState,
  ): string | undefined {
    for (const world of catalog?.worlds ?? []) {
      for (const chapter of [...world.chapters].sort((a, b) => a.chapter - b.chapter)) {
        for (const level of [...chapter.levels].sort((a, b) => a.level - b.level)) {
          if (level.status === 'published' && (state.levels[level.id]?.stars ?? 0) === 0) {
            return level.id;
          }
        }
      }
    }
    return undefined;
  }

  function openLocalGame(): void {
    activeSession = local;
    const levelId = local.snapshot()?.levelId;
    gameScreen.configure({
      session: local,
      mode: 'local',
      onReplay: () => local.restart(),
      onRestart: () => local.restart(),
      onExit: () => app.goHome(),
      ...(levelId === undefined ? {} : { levelId }),
      onWin: recordWin,
      onNext: nextLocalLevel(levelId),
    });
    show('game', gameScreen.element);
    gameScreen.mount();
    // Un clic sur « Jouer » est un geste utilisateur : c'est le moment où le
    // navigateur autorise la création du contexte audio.
    sfx.resume();
  }

  /** Bouton « Niveau suivant » : présent seulement s'il existe une suite. */
  function nextLocalLevel(levelId: string | undefined): (() => void) | null {
    if (levelId === undefined) {
      return null;
    }
    const next = nextLevelId(library.catalog(), levelId);
    if (next === null) {
      return null;
    }
    return () => {
      void playLevelId(next);
    };
  }

  function joinRoom(rawCode: string): void {
    if (account.state() === null) {
      home?.setError('Créez un compte ou connectez-vous pour rejoindre la partie.');
      return;
    }
    const parsed = RoomCodeSchema.safeParse(rawCode);
    if (!parsed.success) {
      home?.setError(`Code invalide : ${rawCode || '(vide)'}`);
      return;
    }
    home?.setError(null);
    net.connect();
    net.joinRoom(parsed.data);
  }

  /**
   * Au chargement : un lien d'invitation `?join=CODE` (QR code) l'emporte sur
   * la session mémorisée ; sinon, on tente de reprendre la place quittée
   * (rechargement de page, onglet fermé pendant la fenêtre de grâce).
   */
  function resumeOrPrefill(): void {
    const code = new URLSearchParams(window.location.search).get('join');
    // Session de CET onglet en priorité : sans elle, un second onglet ouvert
    // sur le lien d'invitation reprendrait la place du premier (les deux
    // partagent `localStorage`).
    const owned = readStoredSession(tabStorage);
    const stored = owned ?? readStoredSession(storage);
    if (code) {
      const parsed = RoomCodeSchema.safeParse(code);
      if (!parsed.success) {
        home?.setError(`Code invalide : ${code}`);
        return;
      }
      home?.setCode(parsed.data);
      if (account.state() === null) {
        // Compte obligatoire : l'invité crée sa session d'abord, la partie
        // est rejointe automatiquement juste après.
        pendingInviteCode = parsed.data;
        home?.setError('Créez un compte ou connectez-vous pour rejoindre la partie.');
        return;
      }
      if (owned && owned.code === parsed.data) {
        // C'est MA partie : l'hôte qui clique sur son propre lien d'invitation
        // (ou l'invité qui le rouvre) doit retrouver SA place, pas en occuper
        // une seconde. Le jeton passe en premier, le code sert de repli.
        pendingJoinCode = parsed.data;
        net.connect();
        net.rejoin(owned);
        return;
      }
      forgetSession();
      joinRoom(parsed.data);
      return;
    }
    if (stored) {
      net.connect();
      net.rejoin(stored);
    }
  }

  /**
   * Retour au premier plan : sur mobile, le navigateur suspend l'onglet en
   * arrière-plan (feuille de partage, changement d'application) et ferme la
   * WebSocket. Sans ces écouteurs, la page revenait vivante mais muette.
   */
  function watchForegroundResume(): void {
    const resume = (): void => net.resume();
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') {
        resume();
      }
    });
    window.addEventListener('pageshow', resume);
    window.addEventListener('focus', resume);
    window.addEventListener('online', resume);
  }

  /**
   * Mode « brouillons » (spec §6) : le jeu est ouvert avec `?drafts=<token>`.
   * Le jeton est rangé dans le stockage de l'onglet et sert d'en-tête Bearer
   * sur l'API de contenu — c'est ainsi qu'un niveau non publié devient visible
   * et testable en jeu, sans jamais l'exposer aux joueurs.
   */
  function readDraftsToken(): string | null {
    let session: Storage | undefined;
    try {
      session = window.sessionStorage;
    } catch {
      session = undefined;
    }
    const fromUrl = new URLSearchParams(window.location.search).get('drafts');
    if (fromUrl) {
      session?.setItem(DRAFTS_TOKEN_STORAGE_KEY, fromUrl);
      return fromUrl;
    }
    return session?.getItem(DRAFTS_TOKEN_STORAGE_KEY) ?? null;
  }

  return app;
}
